import { useEffect, useRef, useState } from 'react';

interface AnimatedCounterProps {
  value: number;
  durationMs?: number;
}

export function AnimatedCounter({ value, durationMs = 350 }: AnimatedCounterProps) {
  const [currentValue, setCurrentValue] = useState(value);
  const [nextValue, setNextValue] = useState(value);
  const [direction, setDirection] = useState<'up' | 'down'>('up');
  const [isAnimating, setIsAnimating] = useState(false);
  const timeoutRef = useRef<number | null>(null);

  useEffect(() => {
    if (value === currentValue) {
      return;
    }
    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current);
    }

    setDirection(value > currentValue ? 'up' : 'down');
    setNextValue(value);
    setIsAnimating(true);

    timeoutRef.current = window.setTimeout(() => {
      setCurrentValue(value);
      setIsAnimating(false);
      setNextValue(value);
    }, durationMs);

    return () => {
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [value, currentValue, durationMs]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, []);

  const currentFormatted = currentValue.toLocaleString();
  const nextFormatted = nextValue.toLocaleString();
  const stateClass = isAnimating ? ' animated-counter--active' : '';

  return (
    <span className={`animated-counter animated-counter--${direction}${stateClass}`}>
      <span className="animated-counter__track">
        <span className="animated-counter__value">{currentFormatted}</span>
        <span className="animated-counter__value">{nextFormatted}</span>
      </span>
    </span>
  );
}
